import moment from 'moment-timezone';
import { capitalize } from 'lodash';
import { isRelativeDate, relativeToMoment } from '../manipulation/date-manipulator.service';
import { DATE_FORMATS, DISPLAY_DATE_FORMATS, UTC_TIMEZONE } from './constants';
import { convertTimestampToMoment, convertDateToMoment } from './conversion';
/**
 * Formats a date value for input display in the date range picker.
 * Handles relative dates (e.g., 'now', 'now-1h'), absolute dates, and timestamps.
 * Uses timezone-aware formatting when timezone is provided.
 *
 * @param date - Date value: Date object, RelativeValue string, or timestamp (number)
 * @param options.timezone - Optional timezone string for timezone-aware formatting
 * @param options.hideSeconds - Drop seconds and milliseconds ('DD.MM.YYYY, HH:mm')
 * @returns Formatted date string in the format 'DD.MM.YYYY, HH:mm:ss.SSS' ('DD.MM.YYYY, HH:mm' with hideSeconds), or 'Now'
 *
 * @example
 * ```ts
 * formatInputValue(new Date(), { timezone: 'UTC' });
 * formatInputValue(1609459200000, { timezone: 'America/New_York' });
 * formatInputValue('now-1h', { timezone: 'UTC', hideSeconds: true });
 * ```
 */
export const formatInputValue = (date, { timezone, hideSeconds = false } = {}) => {
    if (!date) {
        return '';
    }
    const format = hideSeconds ? DATE_FORMATS.INPUT_NO_SECONDS : DATE_FORMATS.INPUT;
    if (isRelativeDate(date) && date.includes('now')) {
        if (date === 'now') {
            return capitalize(date);
        }
        return relativeToMoment(date, timezone).format(format);
    }
    if (!timezone) {
        return moment(date).format(format);
    }
    if (typeof date === 'number') {
        return moment.utc(date).tz(timezone).format(format);
    }
    if (timezone === UTC_TIMEZONE) {
        return moment.utc(date).format(format);
    }
    return moment.tz(date, timezone).format(format);
};
export const normalizeDate = (date, { hideSeconds = false } = {}) => {
    const formats = hideSeconds ? DISPLAY_DATE_FORMATS.noSeconds : DISPLAY_DATE_FORMATS.withSeconds;
    const isCurrentYear = date.year() === moment().year();
    return date.format(isCurrentYear ? formats.currentYear : formats.otherYear);
};
export const normalizeValue = ({ type, value }, { timezone, hideSeconds = false } = {}) => {
    if (type === 'absolute') {
        const date = typeof value === 'number' ? convertTimestampToMoment(value, timezone) : convertDateToMoment(value, timezone);
        return normalizeDate(date, { hideSeconds });
    }
    return capitalize(value);
};
export const formatISO = (value, useUTC = true) => {
    if (useUTC) {
        return moment.utc(value).toISOString();
    }
    return moment(value).toISOString();
};
export const formatWithTimezone = (value, format, timezone) => {
    if (!timezone) {
        return moment(value).format(format);
    }
    if (timezone === UTC_TIMEZONE) {
        return moment.utc(value).format(format);
    }
    if (typeof value === 'number') {
        return moment.utc(value).tz(timezone).format(format);
    }
    return moment.tz(value, timezone).format(format);
};
//# sourceMappingURL=formatting.js.map